'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function OrijinalBotoksAnlamaYontemleri() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme';
  const pageTitle = 'Orijinal Botoksu Anlama Yöntemleri: Güvenli Ürün Nasıl Tespit Edilir?';
  const pageDescription = 'Orijinal botoks nasıl anlaşılır? Sahte botoksun farkı nedir ve kullanılan ürünün güvenli olduğunu nasıl kontrol edebilirsiniz?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Orijinal Botoksu Anlama Yöntemleri', href: '/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Orijinal botoks ürünleri ve güvenlik rehberi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Orijinal Botoksu Anlama Yöntemleri: Güvenli Ürün Nasıl Tespit Edilir?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>11 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Estetik tıpta en çok taklit edilen ürünlerden biri botulinum toksin (botoks)'tur.
                Orijinal olmayan botokslar, etki göstermemesi bir yana, ciddi sağlık riskleri oluşturabilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yazıda orijinal botoksu anlamanın yollarını ve sahte ürünlerin farklarını açıklıyoruz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Türkiye'de Onaylı Botoks Markaları
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4 font-medium">
                  Bu dört marka Türkiye'de yasal olarak satılabilen tek orijinal botulinum toksin ürünleridir.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-blue-200">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="border border-blue-200 px-4 py-3 text-left font-semibold text-blue-900">Marka</th>
                        <th className="border border-blue-200 px-4 py-3 text-left font-semibold text-blue-900">Üretici</th>
                        <th className="border border-blue-200 px-4 py-3 text-left font-semibold text-blue-900">Ülke</th>
                        <th className="border border-blue-200 px-4 py-3 text-left font-semibold text-blue-900">Onay</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-blue-200 px-4 py-3 font-medium text-blue-900">Botox® (Allergan)</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">AbbVie</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">ABD</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">FDA & T.C. Sağlık Bakanlığı</td>
                      </tr>
                      <tr className="bg-blue-25">
                        <td className="border border-blue-200 px-4 py-3 font-medium text-blue-900">Dysport® (Ipsen)</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">Ipsen Biopharm</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">İngiltere</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">FDA & CE</td>
                      </tr>
                      <tr>
                        <td className="border border-blue-200 px-4 py-3 font-medium text-blue-900">Xeomin® (Merz)</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">Merz Pharma</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">Almanya</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">CE & Türkiye onayı</td>
                      </tr>
                      <tr className="bg-blue-25">
                        <td className="border border-blue-200 px-4 py-3 font-medium text-blue-900">Neuronox®</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">Medytox</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">Güney Kore</td>
                        <td className="border border-blue-200 px-4 py-3 text-blue-800">T.C. Sağlık Bakanlığı onayı</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Orijinal Botoksun Ayırt Edici Özellikleri
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  💡 En önemli işaret: Toz formda olmayan botoks orijinal değildir.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-green-200">
                    <thead>
                      <tr className="bg-green-100">
                        <th className="border border-green-200 px-4 py-3 text-left font-semibold text-green-900">Kontrol Noktası</th>
                        <th className="border border-green-200 px-4 py-3 text-left font-semibold text-green-900">Açıklama</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-green-200 px-4 py-3 font-medium text-green-900">Kutu barkodu</td>
                        <td className="border border-green-200 px-4 py-3 text-green-800">Orijinal botoksun her kutusunda üreticiye özel LOT numarası vardır.</td>
                      </tr>
                      <tr className="bg-green-25">
                        <td className="border border-green-200 px-4 py-3 font-medium text-green-900">Hologram etiketi</td>
                        <td className="border border-green-200 px-4 py-3 text-green-800">Allergan, Merz ve Ipsen ürünlerinde renk değişimli hologram bulunur.</td>
                      </tr>
                      <tr>
                        <td className="border border-green-200 px-4 py-3 font-medium text-green-900">Flakon üzeri baskı</td>
                        <td className="border border-green-200 px-4 py-3 text-green-800">Renkli baskı, kabartmasız yüzey — silik yazı sahte işaretidir.</td>
                      </tr>
                      <tr className="bg-green-25">
                        <td className="border border-green-200 px-4 py-3 font-medium text-green-900">Toz yapısı</td>
                        <td className="border border-green-200 px-4 py-3 text-green-800">Orijinal botoks flakonu içinde beyaz, kuru toz halindedir (sıvı değil).</td>
                      </tr>
                      <tr>
                        <td className="border border-green-200 px-4 py-3 font-medium text-green-900">Ambalaj dili</td>
                        <td className="border border-green-200 px-4 py-3 text-green-800">Türkçe prospektüs ve ithalatçı etiketi bulunmalıdır.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Sahte Botoks Nasıl Anlaşılır?
              </h2>
              
              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 mb-4 font-medium">
                  Bu ürünler genellikle internet üzerinden veya kayıt dışı tedarikçilerden temin edilir.
                </p>
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>⚠️ Sıvı veya jel kıvamında ürün</li>
                  <li>⚠️ Hologram bulunmaması</li>
                  <li>⚠️ Düşük fiyatlı "orijinal" iddiası</li>
                  <li>⚠️ Üzerinde "sample", "not for sale", "KFDA use only" ibaresi</li>
                  <li>⚠️ Farklı dilde, eksik etiketli kutular</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Muayenehanelerde Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-3 text-yellow-800">
                  <li>Botoks flakonu hastanın önünde açılmalı</li>
                  <li>Kutu ve flakonun fotoğrafı hasta dosyasına eklenmeli</li>
                  <li>Hekim, kullanılan marka ve LOT numarasını reçeteye yazmalıdır</li>
                  <li>"Sulandırılmış" botoks değil, orijinal flakon kullanılmalıdır</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Ucuz Botoksun Riskleri
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <p className="text-orange-800 mb-4 font-medium">
                  💬 Ucuza yaptırılan sahte botoks, hem cüzdanı hem yüzü yakar.
                </p>
                <ul className="list-disc list-inside space-y-3 text-orange-800">
                  <li>Enfeksiyon ve alerjik reaksiyon</li>
                  <li>Asimetri veya kas felci</li>
                  <li>Etkisiz sonuç (aktif madde azlığı)</li>
                  <li>Ciltte ödem, kızarıklık veya doku ölümü</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Güvenli Uygulama İpuçları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>✅ Her zaman doktor tarafından uygulanmalı</li>
                  <li>✅ Muayenehanenin ruhsatı ve ürün belgeleri görünür olmalı</li>
                  <li>✅ Ürünün açılışı hasta önünde yapılmalı</li>
                  <li>✅ Hekim barkodu dosyanıza yapıştırmalı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Hangi botoks markası en güvenilir?</h3>
                  <p className="text-gray-600">Allergan Botox®, Dysport®, Xeomin® ve Neuronox® resmi onaylı markalardır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Orijinal botoks sıvı mı, toz mu olur?</h3>
                  <p className="text-gray-600">Orijinal botoks her zaman toz formdadır, enjeksiyon öncesi sulandırılır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Sahte botoksun etkisi olur mu?</h3>
                  <p className="text-gray-600">Genellikle etkisizdir veya kas felci riski yaratabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Kutunun orijinal olduğunu nasıl kontrol ederim?</h3>
                  <p className="text-gray-600">Hologram, barkod ve Türkçe ithalatçı etiketi olmalıdır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (U.S. Food and Drug Administration) – "Botulinum Toxin Product List"</li>
                  <li>Harvard Health (2023) – "How to Spot Fake Botox"</li>
                  <li>T.C. Sağlık Bakanlığı – Türkiye İlaç ve Tıbbi Cihaz Kurumu (TİTCK)</li>
                  <li>ISAPS Global Report 2024 – "Authenticity Challenges in Aesthetic Medicine"</li>
                  <li>AbbVie Allergan Official – "Botox Security Features and Hologram Verification"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#orijinalbotoks</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#allergan</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#dysport</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#xeomin</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#güvenlik</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Orijinal Botoks Uygulaması İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan detaylı bilgi alın ve size özel değerlendirme için hemen iletişime geçin.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Orijinal botoks uygulaması hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Güncel botoks fiyatları ve nelere göre değiştiği hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-etkisinin-suresi-ne-kadar-kalicidir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Etkisinin Süresi</h4>
              <p className="text-gray-600 text-sm">Botoksun ne kadar süre etkili olduğu ve kalıcılık faktörleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}